import { Eye, EyeOff, ShieldCheck } from "lucide-react";
import { useUserContext } from "../context/UserContext";
import { useEffect } from "react";

export default function GodModeIndicator() {
  const {
    isGodMode,
    isFreePlanPreview,
    godModeTargetUser,
    deactivateGodMode,
    deactivateFreePlanPreview,
  } = useUserContext();

  // Add padding to body when god mode is active
  useEffect(() => {
    if (isGodMode || isFreePlanPreview) {
      document.body.style.paddingTop = isGodMode ? "80px" : "44px";
    } else {
      document.body.style.paddingTop = "0";
    }

    // Cleanup on unmount
    return () => {
      document.body.style.paddingTop = "0";
    };
  }, [isGodMode, isFreePlanPreview]);

  if (isFreePlanPreview) {
    const handleExitPreview = () => {
      deactivateFreePlanPreview();
      window.location.assign("/admin/users");
    };

    return (
      <div className="fixed inset-x-0 top-0 z-[9999] border-b border-violet-300 bg-violet-100 px-3 py-2 shadow-sm dark:border-violet-800 dark:bg-violet-950">
        <div className="mx-auto flex max-w-8xl items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2 text-violet-950 dark:text-violet-100">
            <Eye className="h-4 w-4 shrink-0" />
            <p className="truncate text-xs sm:text-sm">
              <span className="font-bold">Free plan preview</span>
              <span className="ml-1.5 hidden text-violet-700 dark:text-violet-300 sm:inline">Your account and subscription are unchanged.</span>
            </p>
          </div>
          <button
            type="button"
            onClick={handleExitPreview}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-violet-950 px-2.5 py-1 text-xs font-bold text-white transition-colors hover:bg-violet-800 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-1 dark:bg-violet-200 dark:text-violet-950 dark:hover:bg-white"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            Back to admin
          </button>
        </div>
      </div>
    );
  }

  if (!isGodMode || !godModeTargetUser) {
    return null;
  }

  const handleDeactivateGodMode = async () => {
    try {
      await deactivateGodMode();
    } catch (error) {
      console.error("Failed to deactivate god mode:", error);
      alert("Failed to deactivate god mode");
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 bg-yellow-100 border-b-4 border-yellow-500 p-4 shadow-lg z-[9999]">
      <div className="max-w-8xl mx-auto flex items-center justify-between">
        <div className="flex items-center">
          <Eye className="w-5 h-5 text-yellow-600 mr-2" />
          <div>
            <p className="text-sm font-medium text-yellow-800">
              God Mode Active
            </p>
            <p className="text-sm text-yellow-700">
              Viewing as:{" "}
              <span className="font-semibold">{godModeTargetUser.name}</span> (
              {godModeTargetUser.userId})
            </p>
          </div>
        </div>
        <button
          onPointerDown={handleDeactivateGodMode}
          className="inline-flex items-center px-3 py-1 border border-yellow-500 text-sm font-medium rounded-md text-yellow-700 bg-yellow-50 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors z-[10000]"
        >
          <EyeOff className="w-4 h-4 mr-1" />
          Exit God Mode
        </button>
      </div>
    </div>
  );
}
